import React, { useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import moment from "moment";
import { LuAlertCircle, LuCalendar, LuFileText, LuHash } from "react-icons/lu";
import { toast } from "react-toastify";

import Button from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import Input from "../ui/Input";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogInputClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldLabelClass,
  formFieldIconClass,
  formSelectIconClass,
  segmentActiveClass,
  segmentButtonClass,
  segmentInactiveClass,
  segmentTrackClass,
} from "../ui/styles";
import { POST, PUT } from "../../services/axios.service";
import { formatQty, isWholeQty, sanitizeQty } from "../../static/materialLine";
import {
  signedQty,
  stockTextClass,
  totalStock,
} from "../../static/materialStock";
import { toastStyles } from "../../static/styles";
import {
  IMaterialStockEntryFormInput,
  MaterialStockLedgerRowProps,
  StockMaterial,
} from "../../types/materialStock";

type EntryRow = Extract<MaterialStockLedgerRowProps, { kind: "entry" }>;

type Props = {
  // The material the entry is of, with its sizes' stock.
  material: StockMaterial;
  // The entry being edited, or null for a new one.
  entry: EntryRow | null;
  onClose: () => void;
  onSaved: () => void;
};

// Adds or edits an entry made by hand on a material's stock, at counting or settlement time: bars added (an opening
// stock, a count found higher) or taken (damaged, a count found lower). Opened from the materials list and from the
// material's stock page. The materials module doesn't show sizes: a new entry goes to the material's first size (a new
// material has the one 3660 size), and an edited one keeps its own.
const MaterialStockEntryModal = ({
  material,
  entry,
  onClose,
  onSaved,
}: Props) => {
  const [isSaving, setIsSaving] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IMaterialStockEntryFormInput>({
    defaultValues: {
      material_size_id: entry?.material_size_id ?? material.sizes[0]?.id ?? 0,
      date: moment(entry?.date).format("YYYY-MM-DDTHH:mm"),
      type: entry?.type ?? 0,
      qty: entry ? String(entry.qty) : "",
      note: entry?.note || "",
    },
  });

  const type = useWatch({ control, name: "type" });
  const qty = useWatch({ control, name: "qty" });

  // The material's stock after this entry, shown beside the quantity label as it is typed. An edited entry is already
  // in the stock, so it is taken out first.
  const current = totalStock(material.sizes);
  const base =
    current !== undefined && entry
      ? current - signedQty(entry.type, entry.qty)
      : current ?? 0;
  const after = base + (isWholeQty(qty) ? signedQty(type, Number(qty)) : 0);

  const handleOnSubmit: SubmitHandler<IMaterialStockEntryFormInput> = async (
    data
  ) => {
    setIsSaving(true);

    const body = {
      material_id: material.id,
      material_size_id: data.material_size_id,
      date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
      type: data.type,
      qty: Number(data.qty),
      note: data.note.trim() || null,
    };

    try {
      const response: any = entry
        ? await PUT(`/v1/admin/material-stocks/${entry.id}`, body)
        : await POST(`/v1/admin/material-stocks`, body);

      if (response?.data?.status === "success") {
        toast.success(response.data.message, { ...(toastStyles as any) });
        onSaved();
      } else {
        toast.error(response?.data?.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        { ...(toastStyles as any) }
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent
        size="md"
        className={cx("max-h-[90vh] overflow-y-auto", dialogMobileContentClass)}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <DialogTitle className={dialogTitleClass}>
            {entry ? "સ્ટોક એન્ટ્રી સુધારો" : "સ્ટોક વધ-ઘટ"}
          </DialogTitle>
          <p className="text-sm font-medium text-gray-500">{material.name}</p>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className="space-y-2">
              <label className={fieldLabelClass}>પ્રકાર</label>
              <div className={cx(segmentTrackClass, "grid grid-cols-2")}>
                <button
                  type="button"
                  className={cx(
                    segmentButtonClass,
                    type === 0 ? segmentActiveClass : segmentInactiveClass
                  )}
                  onClick={() => setValue("type", 0)}
                >
                  ઉમેરો
                </button>
                <button
                  type="button"
                  className={cx(
                    segmentButtonClass,
                    type === 1 ? segmentActiveClass : segmentInactiveClass
                  )}
                  onClick={() => setValue("type", 1)}
                >
                  બાદ
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="stock-entry-qty" className={fieldLabelClass}>
                  જથ્થો <span className="text-red-500">*</span>
                </label>
                {current !== undefined && (
                  <span className="text-sm text-gray-500">
                    પછી સ્ટોક:{" "}
                    <span className={cx("font-semibold", stockTextClass(after))}>
                      {formatQty(after)}
                    </span>
                  </span>
                )}
              </div>
              <div className="relative">
                <LuHash className={formFieldIconClass} />
                <Controller
                  control={control}
                  name="qty"
                  rules={{ validate: (value) => isWholeQty(value) }}
                  render={({ field }) => (
                    <Input
                      ref={field.ref}
                      id="stock-entry-qty"
                      name={field.name}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="જથ્થો દાખલ કરો"
                      value={field.value}
                      className={dialogInputClass(!!errors.qty)}
                      onChange={(event) =>
                        field.onChange(sanitizeQty(event.target.value).slice(0, 6))
                      }
                      onBlur={field.onBlur}
                    />
                  )}
                />
                {errors.qty && <LuAlertCircle className={fieldErrorIconClass} />}
              </div>
              {errors.qty && (
                <p className={fieldErrorTextClass}>આખો જથ્થો દાખલ કરો</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="stock-entry-date" className={fieldLabelClass}>
                તારીખ અને સમય <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <LuCalendar className={formSelectIconClass} />
                <Input
                  id="stock-entry-date"
                  type="datetime-local"
                  className={dialogInputClass(!!errors.date)}
                  {...register("date", { required: true })}
                />
                {errors.date && <LuAlertCircle className={fieldErrorIconClass} />}
              </div>
              {errors.date && (
                <p className={fieldErrorTextClass}>તારીખ અને સમય દાખલ કરો</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="stock-entry-note" className={fieldLabelClass}>
                નોંધ
              </label>
              <div className="relative">
                <LuFileText className={formFieldIconClass} />
                <Input
                  id="stock-entry-note"
                  maxLength={255}
                  placeholder="નોંધ દાખલ કરો"
                  className={dialogInputClass()}
                  {...register("note")}
                />
              </div>
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={isSaving}
            >
              બંધ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isSaving}
            >
              {isSaving && <span className={buttonSpinnerClass} />}
              સાચવો
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MaterialStockEntryModal;
